import React, { useState, useEffect, useRef } from 'react';
// Deep Design System paths on purpose — NOT the './Design_System' barrel.
// The barrel re-exports Components/Dropdown/MasterDataSearch, which imports
// redux/actions, and most redux/actions modules import { toast } from
// ToastManager. Going through the barrel would make the toast layer depend on
// the redux layer that already depends on it (import cycle at app boot).
import { AppAlert, AppSnackbar } from './Design_System/Layout/Feedback';
import { AppBox } from './Design_System/Layout/Box';
import { AppIconButton } from './Design_System/Components/Button';
import { AppLabel } from './Design_System/Components/Label';
import AppToolTip from './Design_System/Components/InputBox/AppToolTip';
import {
  CheckCircleIcon,
  ErrorIcon,
  WarningIcon,
  InfoIcon,
  CheckCircleFilledIcon,
  ErrorFilledIcon,
  WarningFilledIcon,
  PinIcon,
  PinOutlinedIcon,
  ExpandMoreIcon,
  ChevronRightIcon,
} from './Design_System/Assets/Icons';
import { colors } from './Design_System/Tokens/Colors';
import { borderRadius } from './Design_System/Tokens/Scales';
import { sizes } from './Design_System/Tokens/Sizes';
import { formatNumber } from '../utils/formatUtils';

const defaultAnchorOrigin = { vertical: 'bottom', horizontal: 'center' };

// Fixed tracks for the log table: status icon, Document, Context,
// Message (flexible), Message Type, pin. Every cell wraps rather than widening.
// Every log payload renders through these same tracks — tagged execution_log
// and untagged log arrays alike — so no page shows a different column set.
const EXEC_LOG_COLUMNS = '22px 110px 220px 1fr 120px 22px';

// Same rhythm as the app's grids at compact density. Used by the log
// tables and by plain-text toasts — see the
// densityCompact rule in DataGridTablePremiumEnhanced.js (minHeight 36 with
// 8px vertical cell padding). columnGap is 2x the 10px horizontal cell padding,
// since a DataGrid puts that padding on both of two adjacent cells.
const COMPACT_METRICS = {
  rowMinHeight: 36,
  paddingY: '8px',
  paddingX: '10px',
  columnGap: '20px',
};

// AppLabel renders a <label>, and bootstrap.min.css (imported globally by
// BSNavbar) gives every label 'margin-bottom: 0.5rem'. That margin is part of
// the grid item's box, so centring it leaves the *text* sitting high and pads
// the row out. Zeroing it here rather than in AppLabel keeps form layouts that
// already rely on that spacing untouched.
const LOG_TABLE_LABEL_SX = { marginBottom: 0 };

const Toast = ({
  open,
  message,
  type = 'info',
  duration = 4000,
  onClose,
  onVisibilityChange,
  pinned = false,
  onTogglePin,
  isDarkTheme = false,
  anchorOrigin = defaultAnchorOrigin,
}) => {
  const didMountRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPinned, setIsPinned] = useState(pinned);
  const [countdownProgress, setCountdownProgress] = useState(100);
  const countdownIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const pinnedRef = useRef(pinned);
  const [expandedPricingSteps, setExpandedPricingSteps] = useState({});
  const togglePricingExpand = (stepId) => {
    setExpandedPricingSteps((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };
  useEffect(() => {
    if (!didMountRef.current) {
      if (isVisible === false) {
        return;
      }
      didMountRef.current = true;
    }

    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
  }, [isVisible]);
  const handleClose = () => {
    if (onVisibilityChange) {
      onVisibilityChange(false);
    }
    setIsVisible(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    setIsPinned(pinned);
    pinnedRef.current = pinned;

    if (pinned && countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
      setCountdownProgress(100);
    }
  }, [pinned]);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsPinned(pinned);
      pinnedRef.current = pinned;
      setCountdownProgress(100);

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      if (!pinned) {
        startTimeRef.current = Date.now();
        countdownIntervalRef.current = setInterval(() => {
          if (!pinnedRef.current && startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, duration - elapsed);
            const progress = (remaining / duration) * 100;
            setCountdownProgress(progress);
            if (remaining <= 0) {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              handleClose();
            }
          }
        }, 50);
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [open, duration, pinned]);

  const handleTogglePin = () => {
    const newPinnedState = !isPinned;
    setIsPinned(newPinnedState);
    pinnedRef.current = newPinnedState;

    if (newPinnedState === false) {
      setCountdownProgress(100);
      if (!countdownIntervalRef.current && isVisible) {
        startTimeRef.current = Date.now();
        countdownIntervalRef.current = setInterval(() => {
          if (!pinnedRef.current && startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, duration - elapsed);
            const progress = (remaining / duration) * 100;
            setCountdownProgress(progress);
            if (remaining <= 0) {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              handleClose();
            }
          }
        }, 50);
      }
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdownProgress(100);
    }

    if (onTogglePin) {
      onTogglePin(newPinnedState);
    }
  };

  const isStructuredArray = Array.isArray(message);
  const isStructuredObject =
    !isStructuredArray &&
    typeof message === 'object' &&
    message &&
    Array.isArray(message.details);

  // This is our new check
  const isFormulaExpression =
    !isStructuredArray &&
    !isStructuredObject &&
    typeof message === 'object' &&
    message &&
    message.__type === 'formula_expression';

  const isPricingLogsData =
    !isStructuredArray &&
    !isStructuredObject &&
    typeof message === 'object' &&
    message &&
    message.__type === 'pricing_logs' &&
    Array.isArray(message.pricing);

  const isRowValidationError =
    typeof message === 'object' &&
    message &&
    message.__type === 'row_validation_error' &&
    Array.isArray(message.results);

  const isSaveResult =
    typeof message === 'object' && message && message.__type === 'save_result';

  const isExecutionLog =
    typeof message === 'object' &&
    message &&
    message.__type === 'execution_log';

  // Branches that render a log table: neutral surface, per-row status icon,
  // pin in the table header.
  const isLogTable = isExecutionLog || isStructuredArray || isStructuredObject;

  // A simple text toast — no table and no structured payload — so it can take
  // the same compact padding as the tables and sit its pin on the text's line.
  const isPlainMessage =
    !isLogTable &&
    !isPricingLogsData &&
    !isRowValidationError &&
    !isSaveResult &&
    !isFormulaExpression;

  const calculateWidth = () => {
    if (isStructuredArray || isStructuredObject || isExecutionLog)
      return '80vw';
    if (isPricingLogsData) {
      return '65vw';
    }
    if (isRowValidationError || isSaveResult) {
      return '25vw';
    }
    if (isFormulaExpression) {
      const { expression, variables } = message;
      const formulaLine = `Formula: ${expression.join(' ')}`;
      const variableLines = Object.entries(variables).map(
        ([key, value]) => `  ${key} = ${value}`,
      );
      const resultLine = `Result: ${expression.join(' ')} = ${message.result}`;

      const allLines = [formulaLine, ...variableLines, resultLine];
      const maxLineLength = Math.max(...allLines.map((line) => line.length));

      const charWidth = 8;
      const maxWidth = 1200;
      const padding = 100;
      const calculatedWidth = Math.min(
        maxWidth,
        maxLineLength * charWidth + padding,
      );
      return `${calculatedWidth}px`;
    }

    const charWidth = 8;
    const maxWidth = 1200;
    let text = '';

    if (typeof message === 'string') {
      text = message;
    } else if (typeof message === 'object' && message !== null) {
      text = JSON.stringify(message);
    } else {
      text = String(message);
    }

    const padding = 120;
    const calculatedWidth = Math.min(
      maxWidth,
      text.length * charWidth + padding,
    );
    return `${calculatedWidth}px`;
  };

  // One row per log detail, for either payload shape: the tagged
  // { __type: 'execution_log', data } wrapper, or a raw log array / single log
  // object that reached the toast untagged (see isStructuredArray/Object).
  // Both come from the same execution-log API, so they project the same fields.
  const buildRowsFromExecutionLog = () => {
    const source = isExecutionLog
      ? message.data
      : isStructuredArray
        ? message
        : isStructuredObject
          ? [message]
          : [];
    const actualSource = Array.isArray(source)
      ? source
      : source
        ? [source]
        : [];
    const rows = [];
    actualSource.forEach((entry) => {
      const details = Array.isArray(entry.details) ? entry.details : [];
      details.forEach((d) => {
        const hasDocumentNumber =
          typeof d.document_number === 'string' &&
          d.document_number.trim() !== '';
        rows.push({
          message: d.message ?? '',
          messageType: d.message_type ?? '',
          context: d.context ?? '',
          documentNumber: hasDocumentNumber ? d.document_number : '',
          docMeta:
            !hasDocumentNumber &&
            d?.document_numbers &&
            typeof d.document_numbers === 'object'
              ? d.document_numbers
              : {},
          createdAt: d.created_at || entry.CreatedAt || entry.created_at || '',
        });
      });
    });
    return rows;
  };

  const formatLabel = (str) => {
    if (!str) return '';
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // const buildRowsFromStructured = () => {
  //   const source = isStructuredArray
  //     ? message
  //     : isStructuredObject
  //       ? [message]
  //       : [];
  //   const rows = [];
  //   source.forEach((entry) => {
  //     const details = Array.isArray(entry.details) ? entry.details : [];
  //     details.forEach((d) => {
  //       rows.push({
  //         logNumber: d.log_number ?? entry.ID ?? entry.log_number,
  //         message: d.message ?? '',
  //         messageType: d.message_type ?? '',
  //         createdAt: d.created_at || entry.CreatedAt || entry.created_at || '',
  //       });
  //     });
  //   });
  //   return rows;
  // };

  // The log table renders one status marker per row instead of a single icon
  // for the whole popup, so message_type has to resolve to a colour + glyph.
  const STATUS_BY_MESSAGE_TYPE = {
    error: 'error',
    failed: 'error',
    failure: 'error',
    warning: 'warning',
    warn: 'warning',
    success: 'success',
    info: 'info',
  };

  const STATUS_ICONS = {
    error: ErrorFilledIcon,
    warning: WarningFilledIcon,
    success: CheckCircleFilledIcon,
    info: InfoIcon,
  };

  // Unrecognised types stay neutral and icon-less, so an unknown value never
  // reads as a real severity.
  const getRowStatus = (messageType) => {
    const key = STATUS_BY_MESSAGE_TYPE[String(messageType || '').toLowerCase()];
    return {
      color: key ? colors.status[key] : colors.text.primary,
      Icon: key ? STATUS_ICONS[key] : null,
    };
  };

  // Chrome for the log table. Every column is a fixed track and
  // every cell wraps (minWidth 0 + overflowWrap), so content that outgrows its
  // column breaks onto the next line instead of stretching the popup.
  const logTableHeaderSx = {
    display: 'grid',
    columnGap: COMPACT_METRICS.columnGap,
    // Fixed band height + centred items puts the headings and the pin on the
    // row's centre line. No margin nudges: a margin on a grid item adds to the
    // row height instead of moving content inside it.
    alignItems: 'center',
    minHeight: `${COMPACT_METRICS.rowMinHeight}px`,
    padding: `0 ${COMPACT_METRICS.paddingX}`,
    // Same white as the value rows; the bottom border alone separates the
    // header, and it stays opaque so rows scroll under the sticky band.
    backgroundColor: colors.background.surface,
    borderBottom: `1px solid ${colors.border.default}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  };

  const logTableRowSx = {
    display: 'grid',
    columnGap: COMPACT_METRICS.columnGap,
    minHeight: `${COMPACT_METRICS.rowMinHeight}px`,
    padding: `${COMPACT_METRICS.paddingY} ${COMPACT_METRICS.paddingX}`,
    backgroundColor: colors.background.surface,
    borderBottom: `1px solid ${colors.border.default}`,
  };

  const logTableCellSx = {
    minWidth: 0,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  };

  const renderRowIcon = (StatusIcon, color) => (
    <AppBox sx={{ display: 'flex', alignItems: 'flex-start' }}>
      {StatusIcon && (
        <StatusIcon sx={{ fontSize: `${sizes.icon.small}px`, color }} />
      )}
    </AppBox>
  );

  // The pin lives in the header row's trailing cell, so it lines up with the
  // column headers instead of floating over the popup.
  const logTablePinCell = (
    <AppBox
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <AppToolTip
        title={isPinned ? 'Unpin to auto-dismiss' : 'Pin to keep visible'}
        PopperProps={{ style: { zIndex: 10000 } }}
      >
        <span>
          <AppIconButton
            variant="iconOnly"
            size="small"
            onClick={handleTogglePin}
            icon={isPinned ? <PinIcon /> : <PinOutlinedIcon />}
            sx={{
              padding: 0,
              '& .MuiSvgIcon-root': {
                fontSize: `${sizes.icon.small}px`,
                // Matches the AppLabel headings beside it: both resolve from
                // the theme's text.secondary, so they stay in step if it changes.
                color: 'text.secondary',
              },
            }}
          />
        </span>
      </AppToolTip>
    </AppBox>
  );

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  };

  const getSeverity = () => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  return (
    <AppSnackbar
      open={isVisible}
      onClose={() => {
        if (isPinned) return;
        handleClose();
      }}
      anchorOrigin={anchorOrigin}
      sx={{
        '& .MuiSnackbar-root': {
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '80vw',
        },
      }}
    >
      <AppAlert
        onClose={isLogTable ? undefined : handleClose}
        severity={getSeverity()}
        // Log tables carry a status icon on every row and the pin in their
        // header, so the popup-level icon and action area are suppressed.
        icon={isLogTable ? false : getIcon()}
        action={
          isLogTable ? undefined : (
            <AppToolTip
              title={isPinned ? 'Unpin to auto-dismiss' : 'Pin to keep visible'}
              PopperProps={{ style: { zIndex: 10000 } }}
            >
              <span>
                <AppIconButton
                  variant="iconOnly"
                  size="small"
                  onClick={handleTogglePin}
                  icon={isPinned ? <PinIcon /> : <PinOutlinedIcon />}
                  sx={{
                    color: 'inherit',
                    '& .MuiSvgIcon-root': {
                      fontSize: `${sizes.icon.small}px`,
                      color: 'inherit',
                    },
                  }}
                />
              </span>
            </AppToolTip>
          )
        }
        sx={{
          width: calculateWidth(),
          maxWidth: '80vw',
          position: 'relative',
          margin: '0 auto',
          '& .MuiAlert-message': {
            fontSize: '0.875rem',
            lineHeight: 1.4,
            whiteSpace: 'pre-line',
            flex: 1,
            width: 'fit-content',
          },
          '& .MuiAlert-action': {
            paddingLeft: 1,
            flexShrink: 0,
          },
          // Both blocks are placed last so they win over the generic
          // .MuiAlert-message / .MuiAlert-action rules above.
          ...(isPlainMessage && {
            padding: `${COMPACT_METRICS.paddingY} ${COMPACT_METRICS.paddingX}`,
            alignItems: 'center',
            // MUI pads the icon 7px vertically and top-aligns the action with a
            // 4px offset and a -8px right margin, which leaves the pin sitting
            // above the text and inset unevenly. Centre both on the text line.
            '& .MuiAlert-icon': {
              padding: 0,
              marginRight: COMPACT_METRICS.paddingX,
              // MUI ships this icon at its default 24px; pin it to the same
              // sizes.icon.small the row icons and the pin use, so every glyph
              // in every toast is one size.
              fontSize: `${sizes.icon.small}px`,
              '& .MuiSvgIcon-root': { fontSize: `${sizes.icon.small}px` },
            },
            '& .MuiAlert-message': {
              padding: 0,
              fontSize: '0.875rem',
              lineHeight: 1.4,
              whiteSpace: 'pre-line',
              flex: 1,
              width: 'fit-content',
            },
            '& .MuiAlert-action': {
              alignItems: 'center',
              padding: 0,
              margin: 0,
              marginLeft: 'auto',
              paddingLeft: COMPACT_METRICS.paddingX,
              flexShrink: 0,
            },
          }),
          ...(isLogTable && {
            backgroundColor: colors.background.surface,
            color: colors.text.primary,
            border: `1px solid ${colors.border.default}`,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            padding: 0,
            overflow: 'hidden',
            '& .MuiAlert-message': {
              padding: 0,
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            },
          }),
        }}
      >
        {/* {!isPinned && !(isStructuredArray || isStructuredObject) && ( */}
        {!isPinned && (
          <AppBox
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              zIndex: 2,
              backgroundColor: isDarkTheme
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.2)',
              borderRadius: `${borderRadius.sm}px ${borderRadius.sm}px 0 0`,
              overflow: 'hidden',
            }}
          >
            <AppBox
              sx={{
                height: '100%',
                width: `${countdownProgress}%`,
                backgroundColor: isDarkTheme
                  ? 'rgba(255, 255, 255, 0.8)'
                  : 'rgba(0, 0, 0, 0.6)',
                transition: 'width 0.05s linear',
                borderRadius: `${borderRadius.sm}px ${borderRadius.sm}px 0 0`,
              }}
            />
          </AppBox>
        )}
        {isPricingLogsData ? (
          <div style={{ maxHeight: 360, overflow: 'auto', width: '100%' }}>
            {/* HEADER GRID */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `40px ${message.columns
                  .map((col) => col.width)
                  .join(' ')}`,
                gap: 6,
                fontWeight: 600,
                paddingBottom: 6,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              <div></div>
              {message.columns.map((col) => (
                <div key={col.key}>{col.label}</div>
              ))}
            </div>

            {/* BODY ROWS */}
            {message.pricing.map((step) => {
              const expanded = expandedPricingSteps[step.id];
              const hasLogs = step.logs && step.logs.length > 0;

              return (
                <div
                  key={step.id}
                  style={{
                    padding: '6px 0',
                    borderBottom: '1px dashed rgba(0,0,0,0.08)',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `40px ${message.columns
                        .map((col) => col.width)
                        .join(' ')}`,
                      gap: 4,
                      alignItems: 'center',
                      cursor: hasLogs ? 'pointer' : 'default',
                    }}
                    onClick={() => hasLogs && togglePricingExpand(step.id)}
                  >
                    {/* ARROW ICON */}
                    <div>
                      {hasLogs && (
                        <AppIconButton
                          variant="iconOnly"
                          size="small"
                          icon={
                            expanded ? (
                              <ExpandMoreIcon fontSize="inherit" />
                            ) : (
                              <ChevronRightIcon fontSize="inherit" />
                            )
                          }
                          sx={{
                            padding: 0,
                            '& .MuiSvgIcon-root': { fontSize: 'inherit' },
                          }}
                        />
                      )}
                    </div>

                    {/* CELL VALUES */}
                    {message.columns.map((col) => (
                      <div key={col.key}>
                        {col.boolean
                          ? step[col.key] === true
                            ? 'True'
                            : 'False'
                          : (step[col.key] ?? '-')}
                      </div>
                    ))}
                  </div>

                  {/* EXPANDED LOG ROWS */}
                  {expanded && hasLogs && (
                    <div
                      style={{
                        marginTop: 4,
                        paddingLeft: 48,
                        borderLeft: `2px solid ${
                          type === 'error'
                            ? 'red'
                            : type === 'warning'
                              ? 'orange'
                              : 'grey'
                        }`,
                      }}
                    >
                      {step.logs.map((l, i) => (
                        <pre
                          key={i}
                          style={{
                            fontSize: '0.8rem',
                            padding: '4px 0',
                            opacity: 0.9,
                            whiteSpace: 'pre-wrap',
                            margin: 0,
                            fontFamily: 'monospace',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: l.message.replace(/\n/g, '<br />'),
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : isRowValidationError ? (
          <div style={{ maxHeight: 300, overflow: 'auto', width: '100%' }}>
            {/* HEADING */}
            <div
              style={{
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {message.error_message}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                gap: 8,
                fontWeight: 600,
                paddingBottom: 6,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              <div>Row Number</div>
              <div>Error</div>
            </div>
            {message.results
              .filter((r) => r.status === 'error')
              .map((r, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr',
                    gap: 8,
                    padding: '6px 0',
                    borderBottom: '1px dashed rgba(0,0,0,0.08)',
                    fontSize: '0.85rem',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>{r.row_number}</div>
                  <div style={{ overflowWrap: 'anywhere' }}>{r.error}</div>
                </div>
              ))}
          </div>
        ) : isSaveResult ? (
          <div style={{ width: '100%' }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 10,
                width: '100%',
              }}
            >
              {message.message || 'File Uploaded Successfully'}
            </div>

            {/* <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                rowGap: 6,
                columnGap: 16,
                fontSize: '0.85rem',
                textAlign: 'center',
              }}
            >
              <div>Total Rows</div>
              <div>{message.total_rows}</div>

              <div>Saved Rows</div>
              <div style={{ color: '#2e7d32', fontWeight: 600 }}>
                {message.success_rows}
              </div> */}

            {/* <div>Failed Rows</div> */}
            {/* <div
                style={{
                  color: message.error_rows > 0 ? '#d32f2f' : 'inherit',
                  fontWeight: message.error_rows > 0 ? 600 : 'normal',
                }}
              >
                {message.error_rows}
              </div> */}
            {/* </div> */}
          </div>
        ) : isLogTable ? (
          <AppBox sx={{ maxHeight: 360, overflow: 'auto' }}>
            <AppBox
              sx={{
                ...logTableHeaderSx,
                gridTemplateColumns: EXEC_LOG_COLUMNS,
              }}
            >
              <AppBox />
              <AppLabel sx={LOG_TABLE_LABEL_SX}>Document</AppLabel>
              <AppLabel sx={LOG_TABLE_LABEL_SX}>Context</AppLabel>
              <AppLabel sx={LOG_TABLE_LABEL_SX}>Message</AppLabel>
              <AppLabel sx={LOG_TABLE_LABEL_SX}>Message Type</AppLabel>
              {logTablePinCell}
            </AppBox>
            {buildRowsFromExecutionLog().map((row, idx) => {
              const { color, Icon: StatusIcon } = getRowStatus(row.messageType);
              return (
                <AppBox
                  key={idx}
                  sx={{
                    ...logTableRowSx,
                    gridTemplateColumns: EXEC_LOG_COLUMNS,
                    color,
                  }}
                >
                  {renderRowIcon(StatusIcon, color)}
                  <AppBox sx={logTableCellSx}>
                    {row.documentNumber
                      ? row.documentNumber
                      : Object.keys(row.docMeta).length > 0 &&
                        Object.entries(row.docMeta).map(([key, val]) => (
                          <AppBox key={key}>
                            {formatLabel(key)}: {val}
                          </AppBox>
                        ))}
                  </AppBox>
                  <AppBox sx={logTableCellSx}>
                    {(() => {
                      if (!row.context) return '-';
                      try {
                        const parsed =
                          typeof row.context === 'string'
                            ? JSON.parse(row.context)
                            : row.context;
                        if (
                          parsed &&
                          typeof parsed === 'object' &&
                          !Array.isArray(parsed)
                        ) {
                          return Object.entries(parsed).map(([key, val]) => (
                            <AppBox key={key}>
                              {formatLabel(key)}: {String(val)}
                            </AppBox>
                          ));
                        }
                      } catch (_) {}
                      return row.context;
                    })()}
                  </AppBox>
                  <AppBox sx={logTableCellSx}>{row.message}</AppBox>
                  <AppBox sx={logTableCellSx}>{row.messageType || ''}</AppBox>
                  <AppBox />
                </AppBox>
              );
            })}
          </AppBox>
        ) : isFormulaExpression ? (
          <div
            style={{
              maxHeight: 360,
              overflow: 'auto',
              background: 'transparent',
            }}
          >
            {(() => {
              const { expression, result, variables } = message;
              const operators = ['+', '-', '*', '/', '(', ')'];
              const formulaLine = `Formula: ${expression.join(' ')}`;
              const resolvedArr = expression.map((element) => {
                if (operators.includes(element)) return element;
                if (!isNaN(element)) {
                  return formatNumber(parseFloat(element));
                }
                const value = variables[element];
                if (value !== undefined && value !== null) {
                  const numValue = parseFloat(value);
                  return !isNaN(numValue) && isFinite(numValue)
                    ? formatNumber(value)
                    : String(value);
                }
                return `?(${element})`;
              });
              const formattedResult =
                result !== undefined && result !== null && !isNaN(result)
                  ? formatNumber(result)
                  : Function(`return ${resolvedArr.join(' ')}`)();
              const resultLine = `Result: ${resolvedArr.join(' ')} = ${formattedResult}`;
              return (
                <div style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
                  <div style={{ fontWeight: 'bold' }}>{formulaLine}</div>
                  <br />
                  <div>
                    {Object.entries(variables).map(([key, value]) => {
                      let formattedValue = 'N/A';
                      if (value !== undefined && value !== null) {
                        const numValue = parseFloat(value);
                        formattedValue =
                          !isNaN(numValue) && isFinite(numValue)
                            ? formatNumber(value)
                            : String(value);
                      }
                      return (
                        <div key={key}>{`  ${key} = ${formattedValue}`}</div>
                      );
                    })}
                  </div>
                  <br />
                  <div style={{ fontWeight: 'bold' }}>{resultLine}</div>
                </div>
              );
            })()}
          </div>
        ) : (
          <>{message}</>
        )}
      </AppAlert>
    </AppSnackbar>
  );
};

export default Toast;
